import {
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useEffect,
  useMemo,
  useState
} from "react";
import { Applicant, Application } from "types/application";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  ApplicationStatus,
  CriteriaRentType,
  Invitee,
  VerificationNames
} from "shared/types";
import { Loader } from "shared/components/Loader";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { ApplicantStatus, ApplicantType } from "shared/types/application";
import { Card } from "shared/components/Card";
import { GroupStats } from "../../components/GroupStats";
import { FaceID } from "untitledui-js-base";
import { Tabs } from "shared/components/Tabs";
import { ApplicantsTable } from "../../components/ApplicantsTable";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import { HugeiconsIcon } from "@hugeicons/react";
import { PauseCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ApplicationOverview } from "../dashboard/ApplicationOverview";
import { getApplicationUIStatus } from "utils/get-application-ui-status";
import { DecisionCard } from "../dashboard/DecisionCard";
import { ApplicationIntelligenceOverview } from "../application-intelligence";
import { FEATURES } from "types/landlord/partner";
import { QueryObserverSuccessResult } from "@tanstack/react-query";
import {
  generateQueries,
  getIncomeGraphsData
} from "views/applications-id/components/IncomeSummary/utils";
import { VerificationData } from "views/applications-id/components/IncomeSummary/types";
import { hasActionInManualApplication } from "views/applications-id/helpers";
import { useSearchParams } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { ExplainRecommendationModal } from "shared/app/ExplainRecommendationModal";

const IncomeSummary = lazy(() => import("../../components/IncomeSummary"));

type TabNames = "Applicants" | "Guarantors" | "Occupants";

export const ApplicationTab = ({
  applicationId,
  applicationMeta,
  canMakeDecision,
  notCancelledApplicants,
  setShowActivityLog,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  onViewTemplate
}: {
  applicationId: string;
  applicationMeta: QueryObserverSuccessResult<Application, Error>;
  canMakeDecision: boolean;
  notCancelledApplicants?: Applicant[];
  setShowActivityLog: Dispatch<SetStateAction<boolean>>;
  setShowDecisionFlowModal: Dispatch<SetStateAction<boolean>>;
  setDecisionConfirmationModal: Dispatch<SetStateAction<boolean>>;
  onViewTemplate?: () => void;
}) => {
  const {
    unit,
    property,
    applicants,
    status,
    verification_template,
    pending_magic_links,
    monthly_rent_amount,
    include_co_signer_in_criteria_calculation,
    requires_overrides_identity_verification,
    rent_amount,
    home_rent_amount,
    site_rent_amount,
    housing_assistance_voucher_amount,
    pet_rent_amount,
    latest_application_rent_history,
    unit_delisted
  } = applicationMeta.data;

  const [, setSearchParams] = useSearchParams();
  const [tab, setActiveTab] = useState<TabNames>("Applicants");
  const [showExplainRecommendations, setShowExplainRecommendations] =
    useState<boolean>(false);
  const { permissions, partner } = useAuthContext();

  const [applicantUsers, coSignerUsers, occupantUsers] = useMemo(() => {
    return applicants.reduce<Applicant[][]>(
      (acc, applicant) => {
        switch (applicant.role) {
          case ApplicantType.CoSigner:
            acc[1].push(applicant);
            break;
          case ApplicantType.Occupant:
            acc[2].push(applicant);
            break;
          default:
            acc[0].push(applicant);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [applicants]);

  const [applicantLinks, coSignerLinks, occupantLinks] = useMemo(() => {
    return (pending_magic_links || []).reduce<(Invitee | Applicant)[][]>(
      (acc, link) => {
        switch (link.role) {
          case ApplicantType.CoSigner:
            acc[1].push(link);
            break;
          case ApplicantType.Occupant:
            acc[2].push(link);
            break;
          default:
            acc[0].push(link);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [pending_magic_links]);

  const availableTabs: TabNames[] = useMemo(() => {
    const res: TabNames[] = [];
    if (applicantLinks.length + applicantUsers.length > 0)
      res.push("Applicants");
    if (coSignerLinks.length + coSignerUsers.length > 0) res.push("Guarantors");
    if (occupantLinks.length + occupantUsers.length > 0) res.push("Occupants");

    return res.length > 0 ? res : [];
  }, [
    coSignerLinks,
    coSignerUsers,
    occupantLinks,
    occupantUsers,
    applicantLinks,
    applicantUsers
  ]);

  const [
    notCancelledApplicantUsers,
    notCancelledCoSignerUsers,
    notCancelledOccupantUsers
  ] = useMemo(() => {
    return (notCancelledApplicants || []).reduce<Applicant[][]>(
      (acc, applicant) => {
        switch (applicant.role) {
          case ApplicantType.CoSigner:
            acc[1].push(applicant);
            break;
          case ApplicantType.Occupant:
            acc[2].push(applicant);
            break;
          default:
            acc[0].push(applicant);
        }
        return acc;
      },
      [[], [], []]
    );
  }, [notCancelledApplicants]);

  const rolesEnabledInSettings = useMemo(() => {
    if (!verification_template?.settings) return [];

    return Object.keys(verification_template.settings).filter(
      (role) => verification_template.settings[role].enabled_screening_services
    ) as ApplicantType[];
  }, [verification_template]);

  const incomeQueries = useMemo(
    () =>
      generateQueries(
        applicants.filter((appl) => {
          if (!include_co_signer_in_criteria_calculation)
            return (
              appl.role === ApplicantType.Applicant &&
              appl.application_status === ApplicantStatus.Submitted
            );
          else return appl.application_status === ApplicantStatus.Submitted;
        }) as Applicant[]
      ),
    [applicants, include_co_signer_in_criteria_calculation]
  );

  const combinedQueryMeta = useMemo(() => {
    return incomeQueries?.reduce(
      (acc, result) => {
        switch (result.verification.name) {
          case VerificationNames.VerifiedIncomeVerification:
            acc.verifiedIncomeData.push(result as VerificationData);
            break;
          case VerificationNames.IncomeVerification:
            acc.selfAttestedIncomeData.push(result as VerificationData);
            break;
          case VerificationNames.CreditReport:
            acc.creditScore.push(result as VerificationData);
            break;
        }
        return acc;
      },
      {
        verifiedIncomeData: [] as VerificationData[],
        selfAttestedIncomeData: [] as VerificationData[],
        creditScore: [] as VerificationData[]
      }
    );
  }, [incomeQueries]);

  const { verifiedIncomeGraphData } = useMemo(
    () => getIncomeGraphsData(combinedQueryMeta?.verifiedIncomeData),
    [combinedQueryMeta]
  );

  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(tab))
      setActiveTab(availableTabs[0]);
  }, [availableTabs]);

  const allowCoSigner = Boolean(
      // INFO: According to Dat it was true because before this field existed, we used to allow cosigners by default
      verification_template?.settings.co_signer.enabled_screening_services ??
      true
    ),
    allowOccupant = Boolean(
      verification_template?.settings.occupant?.enabled_screening_services ??
      false
    );

  const manualApplicantsInProgress = applicants.filter((appl) =>
    hasActionInManualApplication(appl as Applicant)
  );

  const magicLinksCount =
    applicationMeta.data.pending_magic_links?.filter(
      (pml) => pml.active === true
    )?.length || 0;

  const scoreBased =
      verification_template?.recommendation_method === "score_based",
    ruleBased = verification_template?.recommendation_method === "rule_based";

  return (
    <>
      <div className="space-y-6 p-6 pb-0">
        <div className="flex gap-4 lg:flex-nowrap flex-wrap">
          {verification_template ? (
            <DecisionCard
              magicLinksCount={magicLinksCount}
              wrapperClassName="[@media(min-width:1695px)]:hidden w-full max-w-[300px] shrink-0"
              score={applicationMeta.data.score}
              setShowExplainRecommendations={setShowExplainRecommendations}
              setShowDecisionFlowModal={(v) => {
                if (v)
                  status === ApplicationStatus.Submitted
                    ? setShowDecisionFlowModal(true)
                    : setDecisionConfirmationModal(true);
                else {
                  setShowDecisionFlowModal(false);
                  setDecisionConfirmationModal(false);
                }
              }}
              recommendationMethod={verification_template.recommendation_method}
              requiresOverridesIdentityVerification={
                requires_overrides_identity_verification
              }
              applicants={notCancelledApplicants || []}
              canMakeDecision={canMakeDecision}
              ruleReasons={applicationMeta.data.rule_reasons}
              autoApproved={applicationMeta.data.auto_approved}
              criteriaCheckSettings={
                applicationMeta.data.criteria_check_settings
              }
              screeningCriteria={verification_template.screening_criteria}
              applicationStatus={applicationMeta.data.status}
              recommendation={applicationMeta.data.recommendation}
              hasPendingVerifications={
                applicationMeta.data.has_pending_verifications
              }
            />
          ) : null}

          <ApplicationOverview
            screeningStatus={applicationMeta.data.screening_status}
            applicationId={String(applicationId)}
            applicationRentAmount={{
              monthly_rent_amount,
              rent_amount,
              home_rent_amount,
              site_rent_amount,
              housing_assistance_voucher_amount,
              pet_rent_amount
            }}
            setShowActivityLog={setShowActivityLog}
            applicationStatus={
              getApplicationUIStatus({ status, applicants }) ?? status
            }
            latestApplicationRentHistory={latest_application_rent_history}
            unit={unit}
            property={property}
            desiredMoveInDate={applicationMeta.data.desired_move_in_date}
            desiredLeaseTerm={applicationMeta.data.desired_lease_term}
            leaseTermEnabled={applicationMeta.data.lease_term_enabled}
            criteriaRentType={
              verification_template?.criteria_rent_type || CriteriaRentType.Both
            }
            unitDelisted={unit_delisted}
            refetchApplication={applicationMeta.refetch}
            onViewTemplate={onViewTemplate}
            templateName={verification_template?.name}
          />
        </div>
        {partner?.features.includes(FEATURES.AI_APPLICATION_SUMMARY) ? (
          <ApplicationIntelligenceOverview
            applicationId={Number(applicationId)}
            applicants={applicants as Applicant[]}
            hasSubmittedApplicants={applicants.some(
              (appl) => appl.application_status === ApplicantStatus.Submitted
            )}
          />
        ) : null}

        <Show
          if={
            !Boolean(
              requires_overrides_identity_verification?.applicants.length
            ) &&
            [
              ApplicationStatus.Submitted,
              ApplicationStatus.Approved,
              ApplicationStatus.Rejected,
              ApplicationStatus.ConditionalApproved,
              ApplicationStatus.Canceled
            ].includes(status as ApplicationStatus)
          }
        >
          <div className="flex gap-x-4 gap-y-6 flex-wrap lg:flex-nowrap">
            {verification_template ? (
              <DecisionCard
                magicLinksCount={magicLinksCount}
                wrapperClassName="[@media(min-width:1695px)]:block hidden max-w-[300px] w-full"
                score={applicationMeta.data.score}
                setShowExplainRecommendations={setShowExplainRecommendations}
                setShowDecisionFlowModal={(v) => {
                  if (v)
                    status === ApplicationStatus.Submitted
                      ? setShowDecisionFlowModal(true)
                      : setDecisionConfirmationModal(true);
                  else {
                    setShowDecisionFlowModal(false);
                    setDecisionConfirmationModal(false);
                  }
                }}
                recommendationMethod={
                  verification_template.recommendation_method
                }
                requiresOverridesIdentityVerification={
                  requires_overrides_identity_verification
                }
                applicants={notCancelledApplicants || []}
                canMakeDecision={canMakeDecision}
                ruleReasons={applicationMeta.data.rule_reasons}
                autoApproved={applicationMeta.data.auto_approved}
                criteriaCheckSettings={
                  applicationMeta.data.criteria_check_settings
                }
                screeningCriteria={verification_template.screening_criteria}
                applicationStatus={applicationMeta.data.status}
                recommendation={applicationMeta.data.recommendation}
                hasPendingVerifications={
                  applicationMeta.data.has_pending_verifications
                }
              />
            ) : null}

            <GroupStats
              criteriaTypeOptions={
                applicationMeta.data.criteria_check_settings?.credit_score
                  ?.type || CriteriaTypeOptions.AVERAGE
              }
              criteriaSettings={
                verification_template?.screening_criteria?.applicant_settings
              }
              recommendationMethod={
                verification_template?.recommendation_method
              }
              groupStat={applicationMeta.data.group_stat}
              includeCoSignerInCriteriaCalculation={
                include_co_signer_in_criteria_calculation
              }
              applicationId={applicationId || ""}
              applicants={applicants}
              isRuleBased={ruleBased}
            />
          </div>

          <Show if={permissions.canAccessReports.incomeReport}>
            <Suspense
              fallback={
                <Card className="flex items-center justify-center gap-2 h-[106px] mb-6 px-4 bg-white">
                  <Loader />
                </Card>
              }
            >
              <IncomeSummary
                verifiedIncomeGraphData={verifiedIncomeGraphData}
                isLoading={applicationMeta.isLoading}
                isError={applicationMeta.isError}
              />
            </Suspense>
          </Show>
        </Show>
        <Show
          if={Boolean(
            requires_overrides_identity_verification?.applicants.length
          )}
        >
          <Card className="flex items-center gap-2 py-6 px-4">
            <div className="relative m-4">
              <FaceID
                className="size-10 shrink-0 stroke-content-200"
                strokeWidth={1.5}
              />
              <InfoIcon className="absolute -bottom-1.5 -right-1.5 size-6 text-danger-500 bg-white rounded-full" />
            </div>
            <div className="space-y-2">
              <h2 className="text-base font-bold">
                {`Identity verification failed for ${requires_overrides_identity_verification?.applicants
                  .map((applicant) => applicant.name)
                  .join(", ")}`}
              </h2>
              <p className="text-sm">
                You can manually override a failed identity verification run the
                remaining screening services
              </p>
            </div>
          </Card>
        </Show>
        <Show if={Boolean(status === ApplicationStatus.OnHold)}>
          <Card className="flex items-center gap-6 py-8 px-6">
            <HugeiconsIcon
              icon={PauseCircleIcon}
              size={50}
              className="text-content-1000 shrink-0"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <h2 className="text-base font-medium text-content-1000">
                Application is on hold
              </h2>
              <p className="text-sm text-content-300 font-medium">
                Application is on hold for now and can be resumed whenever
                you&apos;re ready
              </p>
            </div>
          </Card>
        </Show>
        <Show
          if={Boolean(
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              undefined
            ].includes(status)
          )}
        >
          {manualApplicantsInProgress.length ? (
            <Card className="h-[110px] mb-6 px-6 py-8 flex gap-6 items-center">
              <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center bg-primary-50">
                <span className="text-base text-primary-500 font-bold">
                  {manualApplicantsInProgress.length}
                </span>
              </div>
              <div>
                <h2 className="text-base font-medium text-content-1000 mb-1">
                  Application{manualApplicantsInProgress.length > 1 ? "s" : ""}{" "}
                  require manual completion{" "}
                </h2>
                <p className="text-sm text-content-300">
                  All in-progress applications must be completed in order to
                  make a decision
                </p>
              </div>

              <Button
                variant={ButtonVariant.PRIMARY}
                className="ml-auto"
                size={ButtonSize.SM}
                onClick={() => {
                  setSearchParams({
                    applicantId: manualApplicantsInProgress[0].id.toString()
                  });
                }}
              >
                Complete application
              </Button>
            </Card>
          ) : null}
        </Show>
        <Show if={availableTabs.length > 0}>
          <div className="mb-4">
            <Tabs
              tabs={availableTabs}
              displayMap={{
                Applicants: (
                  <>
                    Applicants (
                    {applicantLinks.length + notCancelledApplicantUsers.length})
                  </>
                ),
                Guarantors: (
                  <>
                    Guarantors (
                    {coSignerLinks.length + notCancelledCoSignerUsers.length})
                  </>
                ),
                Occupants: (
                  <>
                    Occupants (
                    {occupantLinks.length + notCancelledOccupantUsers.length})
                  </>
                )
              }}
              selected={tab}
              setActiveTab={(tab: TabNames) => setActiveTab(tab)}
              variant="underline"
            />
          </div>
        </Show>
        <Show if={tab === "Applicants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            key="Applicants"
            applicationApplicants={notCancelledApplicants || []}
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            scoreBased={scoreBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Applicant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(applicantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (applicantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>

        <Show if={tab === "Guarantors"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Guarantors"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.CoSigner
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(coSignerUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (coSignerLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
        <Show if={tab === "Occupants"}>
          <ApplicantsTable
            pendingMagicLinks={pending_magic_links || []}
            applicationApplicants={notCancelledApplicants || []}
            key="Occupants"
            availableRoles={rolesEnabledInSettings}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            applicationStatus={status}
            ruleBased={ruleBased}
            applicationId={applicationId}
            templateId={verification_template?.id}
            scoreBased={scoreBased}
            verificationSchemas={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              verification_template?.verification_schemas?.[
                ApplicantType.Occupant
              ]
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            applicants={(occupantUsers || []).concat(
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (occupantLinks || []) as Applicant[]
            )}
            invalidateQueries={["APPLICATION", applicationId]}
            onChangeRole={() => setActiveTab("Applicants")}
            requiresOverridesIdentityVerification={
              requires_overrides_identity_verification
            }
          />
        </Show>
      </div>

      <ExplainRecommendationModal
        application={applicationMeta.data}
        opened={showExplainRecommendations}
        onClose={() => setShowExplainRecommendations(false)}
      />
    </>
  );
};
